// @ts-nocheck
import { useEffect, useState } from 'react';
import { Calendar, Check, Pencil, X } from 'lucide-react';
import { CLOSING_DATE_COLORS, closingDateVariant } from '../../utils/closingDate';
import { pushToast } from '../../utils/toast/toastStore';
import { formatClosingDateBanner } from './CompanySummaryCard';
import { formatIsoYmdForDisplay } from '../../utils/isoDateDisplay';

const formReadonlyClass = 'min-h-9 flex-1 px-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-md text-gray-800';
const formEditBtnClass =
  'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-800';

/** Inline closing date with edit — badge, intake pill, or Transaction form field. */
export function EditableClosingDateChip({
  closingDate,
  onSave,
  disabled = false,
  variant = 'badge',
  fieldLabel = 'Closing Date',
}: {
  closingDate?: string | null;
  onSave: (isoYmd: string) => Promise<void>;
  disabled?: boolean;
  /** `badge` — summary row; `intake` — firm Company Record; `field` — Transaction Details */
  variant?: 'badge' | 'intake' | 'field';
  fieldLabel?: string;
}) {
  const raw = (closingDate ?? '').trim();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) {
      setDraft(raw.length >= 10 ? raw.slice(0, 10) : raw);
    }
  }, [raw, editing]);

  const display = formatClosingDateBanner(raw) || '—';
  const colorClass = raw ? CLOSING_DATE_COLORS[closingDateVariant(raw)] : 'text-gray-500';

  const shellClass =
    variant === 'intake'
      ? 'inline-flex max-w-full items-center gap-1.5 rounded-md bg-white/70 px-2 py-1 text-xs shadow-sm ring-1 ring-gray-200/70'
      : 'inline-flex min-w-0 max-w-full items-baseline gap-1.5 rounded-md border border-gray-200/60 bg-white/80 px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm backdrop-blur-sm';

  const cancel = () => {
    setDraft(raw.length >= 10 ? raw.slice(0, 10) : raw);
    setEditing(false);
  };

  const commit = async () => {
    const next = draft.trim();
    if (!next) return;
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      const msg = e instanceof Error && e.message.trim() ? e.message.trim() : 'Could not save closing date.';
      pushToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (variant === 'field') {
    if (editing) {
      return (
        <div>
          <label className="mb-2 block text-xs font-medium text-gray-700">{fieldLabel}</label>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={saving}
              className="h-9 min-h-9 flex-1 rounded-md border border-gray-300 bg-white px-3 text-xs tabular-nums outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/30"
              aria-label={fieldLabel}
            />
            <button
              type="button"
              onClick={() => void commit()}
              disabled={saving || !draft.trim()}
              className={`${formEditBtnClass} text-emerald-700 hover:bg-emerald-50`}
              title="Save closing date"
              aria-label="Save closing date"
            >
              <Check size={16} aria-hidden />
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={saving}
              className={formEditBtnClass}
              title="Cancel"
              aria-label="Cancel"
            >
              <X size={16} aria-hidden />
            </button>
          </div>
        </div>
      );
    }
    const fieldDisplay = formatIsoYmdForDisplay(raw) || '—';
    return (
      <div>
        <label className="mb-2 block text-xs font-medium text-gray-700">{fieldLabel}</label>
        <div className="flex items-stretch gap-2">
          <div className={formReadonlyClass} title={raw || undefined}>
            {fieldDisplay}
          </div>
          {!disabled ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className={formEditBtnClass}
              title="Edit closing date"
              aria-label="Edit closing date"
            >
              <Pencil size={16} aria-hidden />
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <span className={`${shellClass} items-center`}>
        {variant === 'intake' ? (
          <span className="inline-flex shrink-0 text-emerald-600" aria-hidden>
            <Calendar size={13} strokeWidth={2} />
          </span>
        ) : (
          <span className="shrink-0 text-gray-500">Closing:</span>
        )}
        {variant === 'intake' ? <span className="text-gray-500">Closing</span> : null}
        <input
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={saving}
          className="h-7 min-w-[9.5rem] rounded border border-gray-300 bg-white px-2 text-xs tabular-nums outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/40"
          aria-label="Closing date"
        />
        <button
          type="button"
          onClick={() => void commit()}
          disabled={saving || !draft.trim()}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
          title="Save closing date"
          aria-label="Save closing date"
        >
          <Check size={14} aria-hidden />
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="inline-flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-40"
          title="Cancel"
          aria-label="Cancel"
        >
          <X size={14} aria-hidden />
        </button>
      </span>
    );
  }

  return (
    <span className={shellClass}>
      {variant === 'intake' ? (
        <span className="inline-flex shrink-0 text-emerald-600" title="Closing date" aria-hidden>
          <Calendar size={13} strokeWidth={2} />
        </span>
      ) : null}
      <span className="shrink-0 text-gray-500">{variant === 'intake' ? 'Closing' : 'Closing:'}</span>
      <span className={`tabular-nums ${colorClass}`} title={raw || undefined}>
        {display}
      </span>
      {!disabled ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          title="Edit closing date"
          aria-label="Edit closing date"
        >
          <Pencil size={12} aria-hidden />
        </button>
      ) : null}
    </span>
  );
}
